import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Row, Col, Card, Statistic, Typography, Segmented, Table, Tabs, message, Spin, Tag } from 'antd';
import {
  UserOutlined, DollarOutlined, RiseOutlined, TeamOutlined,
  FundOutlined, FireOutlined, TrophyOutlined, CalendarOutlined
} from '@ant-design/icons';
import {
  AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { analyticsApi } from 'api/analytics';

const { Title, Text } = Typography;

const COLORS = ['#1a7a4f', '#3d9970', '#5bb88a', '#93d4b0', '#c8e8d8'];
const STATUS_COLORS: Record<string, string> = {
  free: '#8c8c8c',
  professional: '#1a7a4f',
  enterprise: '#1890ff',
  free_trial: '#faad14',
};

const PageHeader = styled.div`
  margin-bottom: 24px;
  h2 { margin-bottom: 4px; }
  .subtitle { color: #8c8c8c; font-size: 14px; }
`;

const KpiCard = styled(Card)`
  border-radius: 8px;
  .ant-statistic-title { font-size: 13px; color: #8c8c8c; }
  .ant-statistic-content { font-size: 24px; }
`;

const ChartCard = styled(Card)`
  border-radius: 8px;
  margin-bottom: 16px;
  .ant-card-head-title { font-size: 15px; font-weight: 600; }
`;

const RANGE_OPTIONS = [
  { label: '7d', value: 7 },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
  { label: 'This Year', value: Math.ceil((Date.now() - new Date(new Date().getFullYear(), 0, 1).getTime()) / (24 * 60 * 60 * 1000)) },
  { label: 'All Time', value: 0 },
];

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<any>(null);
  const [growthData, setGrowthData] = useState<any[]>([]);
  const [growthPeriod, setGrowthPeriod] = useState<string>('day');
  const [contentOverview, setContentOverview] = useState<any>(null);
  const [topPlants, setTopPlants] = useState<any[]>([]);
  const [topAnimals, setTopAnimals] = useState<any[]>([]);
  const [engagement, setEngagement] = useState<any>(null);
  const [revenueOverview, setRevenueOverview] = useState<any>(null);
  const [activityData, setActivityData] = useState<any[]>([]);
  const [mostActive, setMostActive] = useState<any[]>([]);
  const [activeSort, setActiveSort] = useState<string>('activity');
  const [rangeDays, setRangeDays] = useState<number>(30);

  useEffect(() => {
    fetchAllData();
  }, [rangeDays]); // eslint-disable-line

  useEffect(() => {
    fetchGrowth(growthPeriod);
  }, [growthPeriod]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [
        overviewRes,
        growthRes,
        contentRes,
        topPlantsRes,
        topAnimalsRes,
        engagementRes,
        revenueRes,
        activityRes,
        mostActiveRes,
      ] = await Promise.all([
        analyticsApi.getOverview(rangeDays || undefined),
        analyticsApi.getUserGrowth('day', 90),
        analyticsApi.getContentOverview(),
        analyticsApi.getTopSpecies('plant', 10),
        analyticsApi.getTopSpecies('animal', 10),
        analyticsApi.getEngagement(),
        analyticsApi.getRevenueOverview(),
        analyticsApi.getContentActivity(90, 'week'),
        analyticsApi.getMostActiveUsers('activity', 10),
      ]);
      setOverview(overviewRes.data);
      setGrowthData(growthRes.data.data || []);
      setContentOverview(contentRes.data);
      setTopPlants(topPlantsRes.data.data || []);
      setTopAnimals(topAnimalsRes.data.data || []);
      setEngagement(engagementRes.data);
      setRevenueOverview(revenueRes.data);
      setActivityData(activityRes.data.data || []);
      setMostActive(mostActiveRes.data.data || []);
    } catch (e: any) {
      console.error('Failed to load admin dashboard:', e);
      message.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchGrowth = async (period: string) => {
    try {
      const res = await analyticsApi.getUserGrowth(period, period === 'month' ? 365 : 90);
      setGrowthData(res.data.data || []);
    } catch (e) {
      console.error('Failed to fetch growth data:', e);
    }
  };

  const fetchMostActive = async (sort: string) => {
    try {
      const res = await analyticsApi.getMostActiveUsers(sort, 10);
      setMostActive(res.data.data || []);
    } catch (e) {
      console.error('Failed to fetch most active users:', e);
    }
  };

  useEffect(() => {
    if (!loading) fetchMostActive(activeSort);
  }, [activeSort]); // eslint-disable-line

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  const statusPieData = overview ? [
    { name: 'Free', value: overview.users_by_status.free, color: STATUS_COLORS.free },
    { name: 'Professional', value: overview.users_by_status.professional, color: STATUS_COLORS.professional },
    { name: 'Enterprise', value: overview.users_by_status.enterprise, color: STATUS_COLORS.enterprise },
    { name: 'Free Trial', value: overview.users_by_status.free_trial, color: STATUS_COLORS.free_trial },
  ].filter(d => d.value > 0) : [];

  const contentPieData = overview ? [
    { name: 'Plants', value: overview.collections_by_type.plant, color: '#1a7a4f' },
    { name: 'Animals', value: overview.collections_by_type.animal, color: '#3d9970' },
  ].filter(d => d.value > 0) : [];

  const speciesColumns = [
    { title: 'Rank', render: (_: any, __: any, i: number) => i + 1, width: 60 },
    { title: 'Common Name', dataIndex: 'common_name', key: 'common_name',
      render: (val: any) => Array.isArray(val) ? val[0] : (val || 'Unknown') },
    { title: 'Scientific Name', dataIndex: 'scientific_name', key: 'scientific_name',
      render: (val: any) => <em>{Array.isArray(val) ? val[0] : (val || 'Unknown')}</em> },
    { title: 'Added', dataIndex: 'count', key: 'count', sorter: (a: any, b: any) => a.count - b.count },
  ];

  const cohortColumns = [
    { title: 'Cohort', dataIndex: 'cohort', key: 'cohort' },
    { title: 'Users', dataIndex: 'users', key: 'users' },
    { title: '30d Retention', dataIndex: 'retained_30d', key: 'retained_30d',
      render: (val: number | null) => val !== null ? `${val}%` : '-' },
    { title: '60d Retention', dataIndex: 'retained_60d', key: 'retained_60d',
      render: (val: number | null) => val !== null ? `${val}%` : '-' },
    { title: '90d Retention', dataIndex: 'retained_90d', key: 'retained_90d',
      render: (val: number | null) => val !== null ? `${val}%` : '-' },
  ];

  return (
    <div>
      <PageHeader>
        <Title level={2}>Admin Dashboard</Title>
        <span className="subtitle">Platform overview and metrics</span>
      </PageHeader>

      {/* Date Range Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, padding: '10px 16px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8 }}>
        <CalendarOutlined style={{ color: '#8c8c8c' }} />
        <Segmented
          options={RANGE_OPTIONS}
          value={rangeDays}
          onChange={(val) => setRangeDays(val as number)}
          size="small"
        />
      </div>

      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={4}>
          <KpiCard>
            <Statistic title="Total Users" value={overview?.total_users || 0}
              prefix={<TeamOutlined />} />
          </KpiCard>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <KpiCard>
            <Statistic title="Paid Users" value={overview?.paid_users || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1a7a4f' }} />
          </KpiCard>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <KpiCard>
            <Statistic title={`New (${rangeDays ? rangeDays + 'd' : 'all'})`} value={overview?.new_users_range ?? overview?.new_users_30d ?? 0}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#1890ff' }} />
          </KpiCard>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <KpiCard>
            <Statistic title={`Active (${rangeDays ? rangeDays + 'd' : 'all'})`} value={overview?.active_users_range ?? overview?.active_users_30d ?? 0}
              prefix={<FireOutlined />}
              valueStyle={{ color: '#faad14' }} />
          </KpiCard>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <KpiCard>
            <Statistic title="MRR" value={revenueOverview?.mrr || 0}
              prefix={<DollarOutlined />} precision={0}
              valueStyle={{ color: '#1a7a4f' }} />
          </KpiCard>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <KpiCard>
            <Statistic title="Conversion" value={overview?.conversion_rate || 0}
              suffix="%" prefix={<FundOutlined />}
              precision={1} />
          </KpiCard>
        </Col>
      </Row>

      {/* User Growth + Status Breakdown */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={16}>
          <ChartCard
            title="User Growth"
            extra={
              <Segmented
                options={[
                  { label: 'Daily', value: 'day' },
                  { label: 'Weekly', value: 'week' },
                  { label: 'Monthly', value: 'month' },
                ]}
                value={growthPeriod}
                onChange={(val) => setGrowthPeriod(val as string)}
                size="small"
              />
            }
          >
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }}
                  tickFormatter={(val) => {
                    if (growthPeriod === 'month') return val;
                    return val.slice(5);
                  }}
                />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Area yAxisId="left" type="monotone" dataKey="new_users" name="New Users"
                  stroke="#1890ff" fill="#1890ff" fillOpacity={0.1} />
                <Area yAxisId="right" type="monotone" dataKey="total_users" name="Total Users"
                  stroke="#1a7a4f" fill="#1a7a4f" fillOpacity={0.1} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>
        <Col xs={24} lg={8}>
          <ChartCard title="Users by Plan">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={statusPieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                  dataKey="value" nameKey="name" label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                >
                  {statusPieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>
      </Row>

      {/* Revenue + Content Split + Engagement */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={8}>
          <ChartCard title="Revenue Metrics">
            <div style={{ padding: '8px 0' }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic title="MRR" value={revenueOverview?.mrr || 0} prefix="$" precision={0} />
                </Col>
                <Col span={12}>
                  <Statistic title="ARR" value={revenueOverview?.arr || 0} prefix="$" precision={0} />
                </Col>
                <Col span={12}>
                  <Statistic title="Churn (30d)" value={revenueOverview?.churn_rate || 0} suffix="%" precision={1}
                    valueStyle={{ color: (revenueOverview?.churn_rate || 0) > 5 ? '#ff4d4f' : '#1a7a4f' }} />
                </Col>
                <Col span={12}>
                  <Statistic title="ARPU" value={revenueOverview?.arpu || 0} prefix="$" precision={2} />
                </Col>
                <Col span={12}>
                  <Statistic title="Churned (30d)" value={revenueOverview?.recent_churns_30d || 0} />
                </Col>
                <Col span={12}>
                  <Statistic title="Ever Paid" value={revenueOverview?.total_ever_paid || 0} />
                </Col>
              </Row>
            </div>
          </ChartCard>
        </Col>
        <Col xs={24} lg={8}>
          <ChartCard title="Plants vs Animals">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={contentPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90}
                  dataKey="value" nameKey="name"
                  label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                >
                  {contentPieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <Row gutter={16} style={{ textAlign: 'center', marginTop: 8 }}>
              <Col span={12}>
                <Text type="secondary">Plant Items</Text>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{overview?.total_plant_items?.toLocaleString()}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Animal Items</Text>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{overview?.total_animal_items?.toLocaleString()}</div>
              </Col>
            </Row>
          </ChartCard>
        </Col>
        <Col xs={24} lg={8}>
          <ChartCard title="Engagement">
            <div style={{ padding: '8px 0' }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic title="DAU" value={engagement?.dau || 0} />
                </Col>
                <Col span={12}>
                  <Statistic title="WAU" value={engagement?.wau || 0} />
                </Col>
                <Col span={12}>
                  <Statistic title="MAU" value={engagement?.mau || 0} />
                </Col>
                <Col span={12}>
                  <Statistic title="DAU/MAU" value={engagement?.dau_mau_ratio || 0} suffix="%" precision={1} />
                </Col>
              </Row>
            </div>
          </ChartCard>
        </Col>
      </Row>

      {/* Activity Chart */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <ChartCard title="Platform Activity (Last 90 Days, Weekly)">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={activityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(val) => val.slice(5)} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="species_added" name="Species Added" fill="#1a7a4f" stackId="a" />
                <Bar dataKey="collection_created" name="Collections" fill="#3d9970" stackId="a" />
                <Bar dataKey="followed_user" name="Follows" fill="#93d4b0" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>
      </Row>

      {/* Most Active Users */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <ChartCard
            title={<><TrophyOutlined style={{ color: '#faad14', marginRight: 8 }} />Most Active Users</>}
            extra={
              <Segmented
                options={[
                  { label: 'By Activity', value: 'activity' },
                  { label: 'Most Lists', value: 'collections' },
                  { label: 'Most Logins', value: 'logins' },
                ]}
                value={activeSort}
                onChange={(val) => setActiveSort(val as string)}
                size="small"
              />
            }
          >
            <Table
              dataSource={mostActive}
              size="small"
              pagination={false}
              rowKey={(r) => r._id || r.email}
              columns={[
                { title: '#', render: (_: any, __: any, i: number) => i + 1, width: 50 },
                { title: 'Email', dataIndex: 'email', key: 'email', ellipsis: true },
                { title: 'Name', dataIndex: 'name', key: 'name', render: (n: string) => n || '-' },
                { title: 'Status', dataIndex: 'status', key: 'status',
                  render: (s: string) => (
                    <Tag color={s === 'professional' ? 'green' : s === 'enterprise' ? 'blue' : s === 'free_trial' ? 'orange' : 'default'}>
                      {s}
                    </Tag>
                  ),
                },
                ...(activeSort === 'activity' ? [
                  { title: 'Actions', dataIndex: 'activity_count', key: 'activity_count',
                    render: (v: number) => v?.toLocaleString() || '0' },
                ] : []),
                ...(activeSort === 'collections' ? [
                  { title: 'Lists', dataIndex: 'total_collections', key: 'total_collections' },
                  { title: 'Plants', dataIndex: 'plants', key: 'plants' },
                  { title: 'Animals', dataIndex: 'animals', key: 'animals' },
                ] : []),
                ...(activeSort === 'logins' ? [
                  { title: 'Logins', dataIndex: 'sign_in_count', key: 'sign_in_count',
                    render: (v: number) => v?.toLocaleString() || '0' },
                ] : []),
                ...(activeSort !== 'collections' ? [
                  { title: 'Lists', dataIndex: 'total_collections', key: 'total_collections',
                    render: (v: number) => v || 0 },
                ] : []),
                { title: 'Last Active', dataIndex: activeSort === 'activity' ? 'last_action' : 'last_activity_date', key: 'last_active',
                  render: (v: string) => v ? new Date(v).toLocaleDateString() : '-' },
              ]}
            />
          </ChartCard>
        </Col>
      </Row>

      {/* Top Species + Retention */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <ChartCard title="Top Species">
            <Tabs
              items={[
                {
                  key: 'plants',
                  label: 'Plants',
                  children: (
                    <Table dataSource={topPlants} columns={speciesColumns}
                      size="small" pagination={false} rowKey="_id" />
                  ),
                },
                {
                  key: 'animals',
                  label: 'Animals',
                  children: (
                    <Table dataSource={topAnimals} columns={speciesColumns}
                      size="small" pagination={false} rowKey="_id" />
                  ),
                },
              ]}
            />
          </ChartCard>
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="Retention Cohorts">
            <Table
              dataSource={engagement?.retention_cohorts || []}
              columns={cohortColumns}
              size="small"
              pagination={false}
              rowKey="cohort"
            />
          </ChartCard>
        </Col>
      </Row>

      {/* Content stats */}
      {contentOverview && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}>
            <KpiCard><Statistic title="Total Collections" value={overview?.total_collections || 0} /></KpiCard>
          </Col>
          <Col xs={12} sm={6}>
            <KpiCard><Statistic title="Shared Collections" value={contentOverview.shared_collections || 0} /></KpiCard>
          </Col>
          <Col xs={12} sm={6}>
            <KpiCard><Statistic title="Uploaded Lists" value={contentOverview.uploaded_collections || 0} /></KpiCard>
          </Col>
          <Col xs={12} sm={6}>
            <KpiCard><Statistic title="Total Species Added" value={overview?.total_species_added || 0} /></KpiCard>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default Dashboard;
